import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserTable = ({ users, onUserSelect, onUserEdit, onUserDelete, onBulkAction, selectedUsers, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    
    // Apply filters
    if (searchTerm) {
      sortableUsers = sortableUsers?.filter(user =>
        user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      sortableUsers = sortableUsers?.filter(user => user?.status === statusFilter);
    }
    
    if (roleFilter !== 'all') {
      sortableUsers = sortableUsers?.filter(user => user?.role === roleFilter);
    }

    // Apply sorting
    if (sortConfig?.key) {
      sortableUsers?.sort((a, b) => {
        if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableUsers;
  }, [users, sortConfig, searchTerm, statusFilter, roleFilter]);

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedUsers?.map(user => user?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleUserSelection = (userId, checked) => {
    if (checked) {
      onSelectionChange([...selectedUsers, userId]);
    } else {
      onSelectionChange(selectedUsers?.filter(id => id !== userId));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      inactive: { color: 'bg-warning text-warning-foreground', label: 'Inactive' },
      suspended: { color: 'bg-error text-error-foreground', label: 'Suspended' },
      pending: { color: 'bg-secondary text-secondary-foreground', label: 'Pending' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-primary text-primary-foreground', label: 'Administrator' },
      manager: { color: 'bg-accent text-accent-foreground', label: 'Manager' },
      editor: { color: 'bg-secondary text-secondary-foreground', label: 'Editor' },
      viewer: { color: 'bg-muted text-muted-foreground', label: 'Viewer' }
    };

    const config = roleConfig?.[role] || roleConfig?.viewer;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filters and Search */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <Input
              type="search"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-80"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="pending">Pending</option>
            </select>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="all">All Roles</option>
              <option value="admin">Administrator</option>
              <option value="manager">Manager</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          
          {selectedUsers?.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('activate')}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Activate ({selectedUsers?.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onBulkAction('deactivate')}
                iconName="XCircle"
                iconPosition="left"
              >
                Deactivate ({selectedUsers?.length})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onBulkAction('delete')}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete ({selectedUsers?.length})
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={selectedUsers?.length === sortedUsers?.length && sortedUsers?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  User
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Role
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Status
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('lastLogin')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Last Login
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Created
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers?.map((user) => (
              <tr key={user?.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <Checkbox
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={(e) => handleUserSelection(user?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{user?.name}</p>
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {getRoleBadge(user?.role)}
                </td>
                <td className="p-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-sm text-foreground">{formatDate(user?.lastLogin)}</p>
                    <p className="text-xs text-muted-foreground">{user?.timezone}</p>
                  </div>
                </td>
                <td className="p-4">
                  <p className="text-sm text-foreground">{formatDate(user?.createdAt)}</p>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserSelect(user)}
                      iconName="Eye"
                      iconSize={16}
                    >
                      <span className="sr-only">View user</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserEdit(user)}
                      iconName="Edit"
                      iconSize={16}
                    >
                      <span className="sr-only">Edit user</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserDelete(user)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-error hover:text-error"
                    >
                      <span className="sr-only">Delete user</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedUsers?.map((user) => (
          <div key={user?.id} className="p-4 border-b border-border last:border-b-0">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={selectedUsers?.includes(user?.id)}
                onChange={(e) => handleUserSelection(user?.id, e?.target?.checked)}
                className="mt-1"
              />
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-foreground">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserSelect(user)}
                      iconName="Eye"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserEdit(user)}
                      iconName="Edit"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onUserDelete(user)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-error"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getRoleBadge(user?.role)}
                  {getStatusBadge(user?.status)}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  <p>Last login: {formatDate(user?.lastLogin)}</p>
                  <p>Created: {formatDate(user?.createdAt)}</p>
                  <p>Timezone: {user?.timezone}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {sortedUsers?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">
            {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' ?'Try adjusting your filters or search terms.' :'Get started by creating your first user account.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UserTable;